// Number / money / "how long ago" texts for the analytics screens (same rules as the dashboard's
// src/modules/dashboard/components/analyticsFormat.js).
import { t } from '@/i18n';

export const num = (v: unknown) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US'));

export const money = (v: unknown) =>
  v === null || v === undefined ? '—' : `${Math.round(Number(v)).toLocaleString('en-US')} ${t('د.ع', 'IQD')}`;

export const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

/** 200 → "3 د 20 ث", 45 → "45 ث", 4000 → "1 س 6 د". */
export const duration = (seconds: unknown) => {
  const s = Math.round(Number(seconds) || 0);
  const [sec, min, hr] = [t('ث', 's'), t('د', 'm'), t('س', 'h')];
  if (s < 60) return `${s} ${sec}`;
  if (s < 3600) return `${Math.floor(s / 60)} ${min}${s % 60 ? ` ${s % 60} ${sec}` : ''}`;
  const m = Math.floor((s % 3600) / 60);
  return `${Math.floor(s / 3600)} ${hr}${m ? ` ${m} ${min}` : ''}`;
};

/** sinceText(5) → "منذ 5 ساعات"; sinceText(isoDate, true) → from a date. */
export const sinceText = (value: unknown, isDate = false) => {
  if (value === null || value === undefined) return '—';
  const hours = isDate ? (Date.now() - new Date(String(value)).getTime()) / 3600000 : Number(value);
  if (hours < 1) return t('منذ أقل من ساعة', 'less than an hour ago');
  if (hours < 24) {
    const h = Math.floor(hours);
    return h === 1 ? t('منذ ساعة', '1 hour ago') : h === 2 ? t('منذ ساعتين', '2 hours ago') : t(`منذ ${h} ساعات`, `${h} hours ago`);
  }
  const d = Math.floor(hours / 24);
  return d === 1 ? t('منذ يوم', '1 day ago') : d === 2 ? t('منذ يومين', '2 days ago') : t(`منذ ${d} أيام`, `${d} days ago`);
};

// Platforms (storefront src/utils/tracking.js decides one per visit).
export const sourceName = (key: string) => {
  const names: Record<string, [string, string]> = {
    direct: ['رابط مباشر', 'Direct link'],
    facebook: ['فيسبوك', 'Facebook'],
    instagram: ['إنستغرام', 'Instagram'],
    google: ['بحث جوجل', 'Google search'],
    google_ads: ['إعلانات جوجل', 'Google Ads'],
    push: ['إشعارات التذكير بالسلة', 'Cart reminders'],
    push_offer: ['إشعارات العروض', 'Offer notifications'],
    tiktok: ['تيك توك', 'TikTok'],
    whatsapp: ['واتساب', 'WhatsApp'],
    telegram: ['تلغرام', 'Telegram'],
    snapchat: ['سناب شات', 'Snapchat'],
    youtube: ['يوتيوب', 'YouTube'],
    search: ['محركات بحث أخرى', 'Other search engines'],
    other: ['مواقع أخرى', 'Other sites'],
  };
  const n = names[key];
  return n ? t(n[0], n[1]) : key || '—';
};

export const deviceName = (key: string) => {
  const names: Record<string, [string, string]> = { mobile: ['موبايل', 'Phone'], tablet: ['تابلت', 'Tablet'], desktop: ['كمبيوتر', 'Computer'] };
  const n = names[key];
  return n ? t(n[0], n[1]) : key || t('غير معروف', 'Unknown');
};

const COUNTRIES: Record<string, string> = {
  IQ: 'العراق', SA: 'السعودية', KW: 'الكويت', JO: 'الأردن', SY: 'سوريا', LB: 'لبنان', TR: 'تركيا', IR: 'إيران',
  AE: 'الإمارات', QA: 'قطر', BH: 'البحرين', OM: 'عُمان', EG: 'مصر', YE: 'اليمن', PS: 'فلسطين', DE: 'ألمانيا',
  GB: 'بريطانيا', US: 'أمريكا', SE: 'السويد', NL: 'هولندا', FR: 'فرنسا', CA: 'كندا', AU: 'أستراليا', AT: 'النمسا',
  DK: 'الدنمارك', NO: 'النرويج', BE: 'بلجيكا', CH: 'سويسرا', IT: 'إيطاليا', ES: 'إسبانيا', RU: 'روسيا',
  PK: 'باكستان', AZ: 'أذربيجان', AM: 'أرمينيا', GE: 'جورجيا', CY: 'قبرص', GR: 'اليونان', MA: 'المغرب', DZ: 'الجزائر',
  TN: 'تونس', LY: 'ليبيا', SD: 'السودان', MY: 'ماليزيا', ID: 'إندونيسيا', CN: 'الصين',
};
export const countryName = (code: unknown) =>
  code ? t(COUNTRIES[String(code).toUpperCase()] || String(code).toUpperCase(), String(code).toUpperCase()) : t('غير معروف', 'Unknown');

// Governorates are stored in English ("Erbil Governorate"): their Arabic / Kurdish names.
const NAMES: Record<string, [string, string]> = {
  baghdad: ['بغداد', 'بەغدا'], basra: ['البصرة', 'بەسرە'], nineveh: ['نينوى', 'نەینەوا'], mosul: ['الموصل', 'مووسڵ'],
  erbil: ['أربيل', 'هەولێر'], hawler: ['أربيل', 'هەولێر'], sulaymaniyah: ['السليمانية', 'سلێمانی'], sulaimaniyah: ['السليمانية', 'سلێمانی'],
  sulaymaniya: ['السليمانية', 'سلێمانی'], slemani: ['السليمانية', 'سلێمانی'], duhok: ['دهوك', 'دهۆک'], dohuk: ['دهوك', 'دهۆک'],
  dahuk: ['دهوك', 'دهۆک'], kirkuk: ['كركوك', 'کەرکووک'], anbar: ['الأنبار', 'ئەنبار'], babil: ['بابل', 'بابل'], babylon: ['بابل', 'بابل'],
  karbala: ['كربلاء', 'کەربەلا'], najaf: ['النجف', 'نەجەف'], qadisiyah: ['القادسية', 'قادسیە'], qadisiyyah: ['القادسية', 'قادسیە'],
  wasit: ['واسط', 'واست'], maysan: ['ميسان', 'مەیسان'], 'dhi qar': ['ذي قار', 'زیقار'], muthanna: ['المثنى', 'موسەننا'],
  diyala: ['ديالى', 'دیالە'], saladin: ['صلاح الدين', 'سەلاحەددین'], 'salah ad din': ['صلاح الدين', 'سەلاحەددین'], halabja: ['حلبجة', 'هەڵەبجە'],
};
const keyOf = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\bgovernorate\b/g, '')
    .replace(/^\s*al[\s-]+/, '')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** "Erbil Governorate" / "erbil" → "أربيل" (unknown names as they are, without "Governorate"). */
export const governorateName = (name: unknown) => {
  if (!name) return '';
  const english = String(name).replace(/\s*Governorate\s*$/i, '');
  const known = NAMES[keyOf(String(name))];
  return known ? t(known[0], english, known[1]) : english;
};

/** A table cell by its column format (backend App\Services\AnalyticsDetail). */
export const formatCell = (value: unknown, format?: string): string => {
  switch (format) {
    case 'number':
      return num(value);
    case 'money':
      return money(value);
    case 'duration':
      return duration(value);
    case 'since':
      return sinceText(value, true);
    case 'percent':
      return `${value}%`;
    case 'source':
      return sourceName(String(value ?? ''));
    case 'device':
      return deviceName(String(value ?? ''));
    case 'country':
      return value ? countryName(value) : '—';
    case 'governorate':
      return value ? governorateName(value) : '—';
    case 'yesno':
      return value ? '✓' : '—';
    default:
      return value === null || value === undefined || value === '' ? '—' : String(value);
  }
};

export const stepName = (step: string) => {
  const names: Record<string, [string, string]> = {
    page_view: ['زاروا الموقع', 'Visited the site'],
    product_view: ['فتحوا منتجاً', 'Opened a product'],
    add_to_cart: ['أضافوا للسلة', 'Added to cart'],
    checkout_start: ['بدأوا الطلب', 'Started an order'],
    purchase: ['أكملوا الطلب', 'Completed the order'],
  };
  const n = names[step];
  return n ? t(n[0], n[1]) : step;
};
